import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@design-system';
import { EditAssumptionsDialog } from '@/features/edit-assumptions';
import { PoliciesComparisonModal } from '@/features/policies-comparison';
import {
  AddPartnerDialog,
  InvitePartnerDialog,
  LinkExistingPartnerDialog,
} from '@/features/add-partner';
import { IllustrationsPanel } from '@/features/shop-insurance/components/illustrations-panel';
import { useClientIllustrations } from '@/features/shop-insurance/hooks/use-client-illustrations';

import {
  Columns3,
  EllipsisVertical,
  FileText,
  Link,
  Mail,
  Settings2,
  Sparkles,
  UserPlus,
} from 'lucide-react';

import { useState } from 'react';
import { useSelector } from 'react-redux';

import { SessionType } from '@shared';
import { selectClient, selectSession, selectFeatureFlags } from '@/model';
import { NylPilotClientCreationRestricted } from '@/shared/components/nyl-pilot-client-creation-restricted';
import { PilotHidden } from '@/shared/components/pilot-restricted';
import { PlanBackButton } from '@/shared/components/plan-back-button';

interface ShopInsuranceHeaderProps {
  isLoading: boolean;
  error: QuoteError | null;
  backLabel: string;
  onClose: () => void;
}

export function ShopInsuranceHeader({
  isLoading,
  error,
  backLabel,
  onClose,
}: ShopInsuranceHeaderProps) {
  const client = useSelector(selectClient);
  const { sessionType } = useSelector(selectSession);
  const featureFlags = useSelector(selectFeatureFlags);
  const hasPolicies = Object.keys(client?.multipleFundingSources?.ltcPolicy ?? {}).length > 0;
  const hasPartner = !!client?.partnerClientId;
  const isAdvisor = sessionType === SessionType.advisor || sessionType === SessionType.wizard;
  const isD2C = sessionType === SessionType.client && !!featureFlags.d2cMode;
  const [openAddPartner, setOpenAddPartner] = useState(false);
  const [openInvitePartner, setOpenInvitePartner] = useState(false);
  const [openLinkExisting, setOpenLinkExisting] = useState(false);
  const [openEditAssumptions, setOpenEditAssumptions] = useState(false);
  const [openComparePolicies, setOpenComparePolicies] = useState(false);
  const [openIllustrations, setOpenIllustrations] = useState(false);
  const isWizard = sessionType === SessionType.wizard;
  const { count: illustrationCount } = useClientIllustrations(
    isWizard ? client?.clientId : undefined,
  );

  const description = isLoading
    ? 'Loading available insurance quotes...'
    : error !== null
      ? 'No quotes available'
      : 'Compare available insurance quotes';

  return (
    <div className="py-4">
      <div className="flex items-start justify-between gap-2">
        <div className="text-left">
          <h1 className="text-primary-dark text-lg font-semibold md:text-xl">
            Explore Coverage Options{' '}
            <Badge className="ml-1 align-middle">
              <Sparkles />
              BETA
            </Badge>
          </h1>
          <p className="text-foreground text-sm md:text-base">{description}</p>
        </div>
        <div className="flex items-center gap-2">
          <PlanBackButton
            label={backLabel}
            onClick={onClose}
            testId="shop-insurance-back"
          />
          {isWizard && (
            <Button
              variant="outline"
              size="sm"
              className="rounded-full"
              onClick={() => setOpenIllustrations(true)}
            >
              <FileText />
              <span className="hidden sm:inline">Illustrations</span>
              {illustrationCount > 0 && (
                <Badge className="rounded-full px-1.5">{illustrationCount}</Badge>
              )}
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger className="focus-visible:outline-none">
              <Button
                variant="outline"
                size="icon-sm"
                className="rounded-full"
              >
                <EllipsisVertical />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={() => setOpenEditAssumptions(true)}>
                <Settings2 />
                Edit Assumptions
              </DropdownMenuItem>
              {hasPolicies && (
                <PilotHidden capability="policies-comparison">
                  <DropdownMenuItem onClick={() => setOpenComparePolicies(true)}>
                    <Columns3 />
                    Compare Policies
                  </DropdownMenuItem>
                </PilotHidden>
              )}
              {!hasPartner && !isD2C && isAdvisor && (
                <DropdownMenuItem onClick={() => setOpenLinkExisting(true)}>
                  <Link />
                  Link Existing Client as Partner
                </DropdownMenuItem>
              )}
              {!hasPartner && !isD2C && (
                <NylPilotClientCreationRestricted capability="add-partner">
                  <DropdownMenuItem onClick={() => setOpenAddPartner(true)}>
                    <UserPlus />
                    Add new partner
                  </DropdownMenuItem>
                </NylPilotClientCreationRestricted>
              )}
              {!hasPartner && !isD2C && !isAdvisor && (
                <DropdownMenuItem onClick={() => setOpenInvitePartner(true)}>
                  <Mail />
                  Invite Partner
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <AddPartnerDialog
        open={openAddPartner}
        onOpenChange={setOpenAddPartner}
        clientId={client.clientId}
      />

      <InvitePartnerDialog
        open={openInvitePartner}
        onOpenChange={setOpenInvitePartner}
      />

      {isAdvisor && (
        <LinkExistingPartnerDialog
          open={openLinkExisting}
          client={client}
          onClose={() => setOpenLinkExisting(false)}
        />
      )}

      <EditAssumptionsDialog
        open={openEditAssumptions}
        onOpenChange={setOpenEditAssumptions}
      />

      <PoliciesComparisonModal
        open={openComparePolicies}
        onOpenChange={setOpenComparePolicies}
      />

      {isWizard && (
        <IllustrationsPanel
          clientId={client?.clientId}
          open={openIllustrations}
          onOpenChange={setOpenIllustrations}
        />
      )}
    </div>
  );
}
